import {
  Button,
  Icons,
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@ais-ui/ui-core';
import React, { ElementType, useMemo, useState } from 'react';
import {
  PiBoat,
  PiFileCsv,
  PiFileImage,
  PiFilePdf,
  PiFileZip,
  PiMicrosoftExcelLogo,
  PiShapes,
  PiSquareSplitHorizontal,
} from 'react-icons/pi';

import { ParsedReportParams, ReportTemplate } from '@ais-ui/websockets';
import { CloneReport } from './Mutations/CloneReport/CloneReport';

import { usePermissions } from '../Permissions/PermissionsProvider';
import { useLocalization } from '@ais-ui/locale-management';
import { useExportReport } from './useExportReport';
import { GiAbstract041 } from 'react-icons/gi';

interface ReportActionButtonProps {
  id: string;
  isLoading?: boolean;
  isDeactivated?: boolean;
  Icon: ElementType;
  onClick?: () => void;
  text: string;
}

const ReportActionButton = React.forwardRef<
  HTMLButtonElement,
  ReportActionButtonProps
>(({ id, isLoading, isDeactivated, Icon, onClick, text }, ref) => {
  return (
    <Button
      ref={ref}
      id={id}
      variant="ghost"
      onClick={onClick}
      disabled={isDeactivated}
      className="flex gap-x-2 col-span-2 justify-start"
    >
      {!isLoading ? (
        <Icon></Icon>
      ) : (
        <Icons.spinner className="h-4 w-4 animate-spin" />
      )}
      {text}
    </Button>
  );
});

interface ReportDetailActionsMenuProps {
  reportTemplate: ReportTemplate;
  report: ParsedReportParams | undefined;
  jobId: string;
  exportTypes: string[] | undefined;
}
export const ReportDetailActionsMenu = ({
  reportTemplate,
  report,
  jobId,
  exportTypes,
}: ReportDetailActionsMenuProps) => {
  const { messages } = useLocalization();
  const permissions = usePermissions();

  const hasClonePermission = useMemo(
    () => permissions[reportTemplate.type],
    [reportTemplate.type, permissions]
  );
  const [showPopover, setShowPopover] = useState<boolean>(false);
  const { onExport, activeExportFormat } = useExportReport(jobId, report?.info);

  return (
    <div className="flex w-full justify-end">
      <Popover open={showPopover} onOpenChange={setShowPopover}>
        <PopoverTrigger asChild>
          <Button disabled={!report}>
            {messages.reportsDetailsActionsButtonText}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-96" side="left" align="end">
          <div className="grid gap-4">
            <div className="space-y-2">
              <h4 className="font-medium leading-none">
                {messages.reportsDetailsActionsTitle}
              </h4>
              <p className="text-sm text-muted-foreground">
                {messages.reportsDetailsActionsSubtitle}
              </p>
            </div>
            <div className="grid gap-2"></div>
          </div>
          <div className="grid grid-cols-4">
            <CloneReport
              setShowPopover={setShowPopover}
              report={report}
              reportTemplate={reportTemplate}
            >
              <ReportActionButton
                id="clone-report-button"
                Icon={PiSquareSplitHorizontal}
                isDeactivated={!!activeExportFormat || !hasClonePermission}
                text={messages.reportsDetailsActionsCloneText}
              ></ReportActionButton>
            </CloneReport>
            {exportTypes?.includes('OF_PDF') && (
              <ReportActionButton
                onClick={() => onExport('OF_PDF')}
                id="pdf-export"
                isLoading={activeExportFormat === 'OF_PDF'}
                isDeactivated={!!activeExportFormat}
                Icon={PiFilePdf}
                text={
                  activeExportFormat === 'OF_PDF'
                    ? messages.reportsDetailsActionsExportingPdfText
                    : messages.reportsDetailsActionsExportPdfText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_CSV') && (
              <ReportActionButton
                onClick={() => onExport('OF_CSV')}
                id="csv-export"
                isLoading={activeExportFormat === 'OF_CSV'}
                isDeactivated={!!activeExportFormat}
                Icon={PiFileCsv}
                text={
                  activeExportFormat === 'OF_CSV'
                    ? messages.reportsDetailsActionsExportingCsvText
                    : messages.reportsDetailsActionsExportCsvText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_EXCEL') && (
              <ReportActionButton
                onClick={() => onExport('OF_EXCEL')}
                id="excel-export"
                isLoading={activeExportFormat === 'OF_EXCEL'}
                isDeactivated={!!activeExportFormat}
                Icon={PiMicrosoftExcelLogo}
                text={
                  activeExportFormat === 'OF_EXCEL'
                    ? messages.reportsDetailsActionsExportingExcelText
                    : messages.reportsDetailsActionsExportExcelText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_ZIP') && (
              <ReportActionButton
                onClick={() => onExport('OF_ZIP')}
                id="zip-export"
                isLoading={activeExportFormat === 'OF_ZIP'}
                isDeactivated={!!activeExportFormat}
                Icon={PiFileZip}
                text={
                  activeExportFormat === 'OF_ZIP'
                    ? messages.reportsDetailsActionsExportingZipText
                    : messages.reportsDetailsActionsExportZipText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_ESRI_SHAPES') && (
              <ReportActionButton
                onClick={() => onExport('OF_ESRI_SHAPES')}
                id="shapefile-export"
                isLoading={activeExportFormat === 'OF_ESRI_SHAPES'}
                isDeactivated={!!activeExportFormat}
                Icon={PiShapes}
                text={
                  activeExportFormat === 'OF_ESRI_SHAPES'
                    ? messages.reportsDetailsActionsExportingShapefileText
                    : messages.reportsDetailsActionsExportShapefileText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_ESRI_RASTER') && (
              <ReportActionButton
                onClick={() => onExport('OF_ESRI_RASTER')}
                id="raster-export"
                isLoading={activeExportFormat === 'OF_ESRI_RASTER'}
                isDeactivated={!!activeExportFormat}
                Icon={PiFileImage}
                text={
                  activeExportFormat === 'OF_ESRI_RASTER'
                    ? messages.reportsDetailsActionsExportingRasterfileText
                    : messages.reportsDetailsActionsExportRasterfileText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_SHIPLIST') && (
              <ReportActionButton
                onClick={() => onExport('OF_SHIPLIST')}
                id="shiplist-export"
                isLoading={activeExportFormat === 'OF_SHIPLIST'}
                isDeactivated={!!activeExportFormat}
                Icon={PiBoat}
                text={
                  activeExportFormat === 'OF_SHIPLIST'
                    ? messages.reportsDetailsActionsExportingShiplistText
                    : messages.reportsDetailsActionsExportShiplistText
                }
              ></ReportActionButton>
            )}
            {exportTypes?.includes('OF_CSV_TRACK') && (
              <ReportActionButton
                onClick={() => onExport('OF_CSV_TRACK')}
                id="csv-track-export"
                isLoading={activeExportFormat === 'OF_CSV_TRACK'}
                isDeactivated={!!activeExportFormat}
                Icon={GiAbstract041}
                text={
                  activeExportFormat === 'OF_CSV_TRACK'
                    ? messages.reportsDetailsActionsExportingCsvTrackText
                    : messages.reportsDetailsActionsExportCsvTrackText
                }
              ></ReportActionButton>
            )}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
};
